// Global
import React, { useEffect, useRef, useState } from 'react';

// Local
import styles from './NewsletterSignup.module.scss';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { Themes } from 'lib/themes';
import { useNewsletterSignup } from 'lib/hooks/useNewsletterSignup';
import { Subscriptions } from 'lib/services/newsletter-signup-service';
import { sendNewsletterOptInAnalytics } from 'components/helpers/Analytics/NewsletterSignupAnalytics';
import LinkWrapper from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { LinkField, LinkFieldValue } from '@sitecore-content-sdk/nextjs';
import { SvgIcon } from 'components/helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';
import { getDefaultChooseOneValue } from 'lib/utils/question-options';

export interface NewsletterSignupProps {
  theme?: Themes;
  headline?: React.ReactNode;
  description?: React.ReactNode;
  image?: React.ReactNode;
  hasImage?: boolean;
  emailLabel?: React.ReactNode;
  emailPlaceholder?: string;
  businessIndustryLabel?: React.ReactNode;
  businessIndustryPlaceholder?: string;
  businessIndustryOptions?: { label: string; value: string }[];
  showBusinessIndustryDropdown?: boolean;
  businessIndustryRequired?: boolean;
  businessIndustryQuestionId?: string;
  submitButton?: React.ReactNode;
  legalText?: React.ReactNode;
  successHeadline?: React.ReactNode;
  successDescription?: React.ReactNode;
  emailErrorMessage: string;
  businessIndustryErrorMessage?: string;
  subscriptions?: Subscriptions[];
  loginLink?: (email: string) => LinkField | LinkFieldValue | undefined;
  // Analytics props
  newsletterTitle?: string;
  subscriberPosition?: string;
  successHeadlineForLoggedIn?: string;
}

export const NewsletterSignup = (props: NewsletterSignupProps) => {
  const hasImage = props.hasImage ?? !!props.image;
  const defaultBusinessIndustryValue = getDefaultChooseOneValue(props.businessIndustryOptions);

  /* Hooks */
  const { data: session } = useSessionSafe();
  const { loginLink } = props;
  const { getDictionaryValue } = useDictionary();

  // Get dictionary values for server error
  const serverErrorHeading =
    getDictionaryValue('Components.NewsletterSignup.ServerError') ||
    'There was an issue completing your request.';
  const serverErrorBody =
    getDictionaryValue('Components.NewsletterSignup.ServerErrorSubHeadline') || 'Please try again.';

  // Analytics callback for successful newsletter signup
  const handleNewsletterSuccess = (subscriptions?: Subscriptions[]) => {
    if (subscriptions && subscriptions.length > 0) {
      const newsletterTitle = props.newsletterTitle || 'Newsletter';
      sendNewsletterOptInAnalytics({
        newsletterTitles: subscriptions.map(() => newsletterTitle),
        newsletterIds: subscriptions.map((s) => s.SubscriptionId),
        subscriberPosition: props.subscriberPosition || 'NewsletterSignup',
      });
    }
  };

  const emailInputRef = useRef<HTMLInputElement>(null);
  const businessIndustrySelectRef = useRef<HTMLSelectElement>(null);
  const [submitAttemptCount, setSubmitAttemptCount] = useState(0);

  const handleFormSubmit = async (e: React.FormEvent) => {
    await handleSubmit(e);
    setSubmitAttemptCount((currentCount) => currentCount + 1);
  };

  const {
    isSubmitting,
    isSuccess,
    error,
    shouldShowComponent,
    isCheckingSubscription,
    isLoggedIn,
    handleEmailChange,
    handleSubmit,
    email,
    submittedEmail,
    emailError,
    businessIndustry,
    businessIndustryError,
    handleBusinessIndustryChange,
  } = useNewsletterSignup(session, props.subscriptions, props.emailErrorMessage, {
    // Only enforce "required" when the dropdown is actually rendered, so the hook
    // never blocks submission for a field the user can't see.
    businessIndustryRequired:
      !!props.showBusinessIndustryDropdown && !!props.businessIndustryRequired,
    businessIndustryErrorMessage: props.businessIndustryErrorMessage,
    businessIndustryQuestionId: props.businessIndustryQuestionId,
    defaultBusinessIndustryValue,
    onSuccess: handleNewsletterSuccess,
  });

  useEffect(() => {
    if (!submitAttemptCount || isLoggedIn || isSubmitting || isSuccess) {
      return;
    }

    if (emailError || error) {
      emailInputRef.current?.focus();
    } else if (businessIndustryError) {
      businessIndustrySelectRef.current?.focus();
    }
    // Only run when submitAttemptCount changes (i.e. on submit), not on every error state change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttemptCount, isLoggedIn, isSubmitting, isSuccess]);

  // Build the banner message by aggregating field errors, matching the SignUp pattern:
  // the generic body line is followed by the de-duplicated field error messages.
  const bannerFieldErrors = Array.from(
    new Set(
      [
        emailError ? props.emailErrorMessage : '',
        businessIndustryError,
        !emailError && error ? error : '',
      ].filter(Boolean) as string[]
    )
  ).join(' ');
  const serverErrorBodyLine = [serverErrorBody, bannerFieldErrors].filter(Boolean).join(' ');

  // Don't render component if user is already subscribed or completed signup
  if (!shouldShowComponent) {
    return null;
  }

  // Show loading state while checking subscription status
  if (isLoggedIn && isCheckingSubscription) {
    return null;
  }

  return (
    <BrandAndThemeProvider theme={props.theme}>
      <div
        data-component="authorable/shared/newsletter-signup/newsletter-signup"
        className={styles.newsletterSignup}
        role="region"
      >
        <div className={styles.contentWrapper}>
          {/* Content */}
          <div
            className={styles.copyContainer + (!hasImage ? ' ' + styles.copyContainerNoImage : '')}
          >
            {!isSuccess && (
              <div className={styles.copy + (!hasImage ? ' ' + styles.copyNoImage : '')}>
                {props.headline && <h2 className={styles.headline}>{props.headline}</h2>}

                {props.description && <div className={styles.description}>{props.description}</div>}
              </div>
            )}

            {isSuccess ? (
              <div className={styles.successState} role="status">
                {props.successHeadline && (
                  <h2 className={styles.successHeadline}>{props.successHeadline}</h2>
                )}
                {props.successDescription && (
                  <div className={styles.successDescription}>{props.successDescription}</div>
                )}
                {isLoggedIn ? (
                  <div className={styles.subscribedMessage} aria-live="assertive">
                    {props.successHeadlineForLoggedIn}
                  </div>
                ) : (
                  loginLink && (
                    <div className={styles.loginLink} role="navigation">
                      <LinkWrapper
                        className={styles.loginLink}
                        field={loginLink(submittedEmail)}
                        ctaStyle="link"
                        ctaVariant="brand"
                      />
                    </div>
                  )
                )}
              </div>
            ) : (
              <>
                <form
                  onSubmit={handleFormSubmit}
                  noValidate
                  className={
                    styles.formContainer + (!hasImage ? ' ' + styles.formContainerNoImage : '')
                  }
                >
                  {/* Server Error Message */}
                  {!!(error || emailError || businessIndustryError) && (
                    <div className={styles.serverErrorContainer}>
                      <div className={styles.serverErrorMessage} role="alert" aria-live="assertive">
                        <div className={styles.serverErrorIcon}>
                          <SvgIcon icon="info" size="sm" aria-hidden="true" />
                        </div>
                        <div className={styles.serverErrorContent}>
                          <div className={styles.serverErrorHeading}>{serverErrorHeading}</div>
                          <div className={styles.serverErrorBody}>{serverErrorBodyLine}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={styles.form}>
                    {(props.showBusinessIndustryDropdown || !isLoggedIn) && (
                      <span className={styles.requiredField}>
                        {getDictionaryValue('Components.NewsletterSignup.Required', '* Required')}
                      </span>
                    )}

                    {/* Only show email input for anonymous users */}
                    {!isLoggedIn && (
                      <>
                        {props.emailLabel && (
                          <label htmlFor="newsletter-email-input" className={styles.emailLabel}>
                            {props.emailLabel}
                            <span> *</span>
                          </label>
                        )}
                        <div className={styles.inputRow}>
                          <input
                            id="newsletter-email-input"
                            ref={emailInputRef}
                            type="email"
                            value={email}
                            onChange={handleEmailChange}
                            placeholder={props.emailPlaceholder}
                            required
                            disabled={isSubmitting}
                            className={
                              styles.emailInput +
                              (emailError || error ? ' ' + styles.emailInputErrorHighlight : '')
                            }
                            aria-invalid={!!(emailError || error)}
                            aria-describedby={
                              emailError || error ? 'newsletter-email-error' : undefined
                            }
                          />
                          {emailError && (
                            <div id="newsletter-email-error" className={styles.emailInputError}>
                              {emailError}
                            </div>
                          )}
                          {error && !emailError && (
                            <div id="newsletter-email-error" className={styles.emailInputError}>
                              {error}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {props.showBusinessIndustryDropdown && (
                      <>
                        <label
                          htmlFor="newsletter-business-industry-select"
                          className={styles.emailLabel}
                        >
                          {props.businessIndustryLabel || 'Business Industry'}
                          {props.businessIndustryRequired && <span> *</span>}
                        </label>
                        <div className={styles.inputRow}>
                          <div
                            className={
                              styles.selectWrapper +
                              (businessIndustryError ? ' ' + styles.emailInputErrorHighlight : '')
                            }
                          >
                            <select
                              id="newsletter-business-industry-select"
                              ref={businessIndustrySelectRef}
                              value={businessIndustry}
                              onChange={handleBusinessIndustryChange}
                              required={!!props.businessIndustryRequired}
                              disabled={isSubmitting}
                              className={styles.businessIndustrySelect}
                              aria-invalid={!!businessIndustryError}
                              aria-describedby={
                                businessIndustryError
                                  ? 'newsletter-business-industry-error'
                                  : undefined
                              }
                            >
                              {!defaultBusinessIndustryValue && (
                                <option value="">{props.businessIndustryPlaceholder}</option>
                              )}
                              {(props.businessIndustryOptions || []).map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <span className={styles.selectIcon} aria-hidden="true">
                              <SvgIcon icon="chevron-down" size="sm" />
                            </span>
                          </div>
                          {businessIndustryError && (
                            <div
                              id="newsletter-business-industry-error"
                              className={styles.emailInputError}
                            >
                              {businessIndustryError}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Show error for logged-in users if any */}
                    {isLoggedIn && error && (
                      <div className={styles.emailInputError} role="alert">
                        {error}
                      </div>
                    )}
                  </div>

                  {props.legalText && (
                    <div className={styles.legalText} role="note">
                      {props.legalText}
                    </div>
                  )}

                  {props.submitButton && (
                    <div className={styles.submitButton}>{props.submitButton}</div>
                  )}
                </form>
              </>
            )}
          </div>

          {/* Image */}
          {hasImage && <div className={styles.imageWrapper}>{props.image}</div>}
        </div>
      </div>
    </BrandAndThemeProvider>
  );
};
